import { createContext, useContext, useRef, useState, useMemo, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";

const PageHeaderContext = createContext(null);

const useHeaderSlot = (slotRef, setReady) =>
  useCallback((el) => {
    slotRef.current = el;
    setReady(!!el);
  }, [slotRef, setReady]);

export const PageHeaderProvider = ({ children }) => {
  const leadingActionsRef = useRef(null);
  const actionsRef = useRef(null);
  const rightActionsRef = useRef(null);
  const [leadingReady, setLeadingReady] = useState(false);
  const [ready, setReady] = useState(false);
  const [rightReady, setRightReady] = useState(false);
  const [titleSuffix, setTitleSuffix] = useState(null);
  const [titleOverride, setTitleOverride] = useState(null);
  const [descriptionOverride, setDescriptionOverride] = useState(null);
  const value = useMemo(
    () => ({
      leadingActionsRef,
      setLeadingReady,
      leadingReady,
      actionsRef,
      setReady,
      ready,
      rightActionsRef,
      setRightReady,
      rightReady,
      titleSuffix,
      setTitleSuffix,
      titleOverride,
      setTitleOverride,
      descriptionOverride,
      setDescriptionOverride,
    }),
    [leadingReady, ready, rightReady, titleSuffix, titleOverride, descriptionOverride],
  );

  return (
    <PageHeaderContext.Provider value={value}>
      {children}
    </PageHeaderContext.Provider>
  );
};

export const PageHeaderTargetLeading = ({ className }) => {
  const ctx = useContext(PageHeaderContext);
  const setRef = useHeaderSlot(ctx?.leadingActionsRef, ctx?.setLeadingReady);

  return <div ref={setRef} className={className} />;
};

export const PageHeaderTarget = ({ className }) => {
  const ctx = useContext(PageHeaderContext);
  const setRef = useHeaderSlot(ctx?.actionsRef, ctx?.setReady);

  return <div ref={setRef} className={className} />;
};

export const PageHeaderTargetRight = ({ className }) => {
  const ctx = useContext(PageHeaderContext);
  const setRef = useHeaderSlot(ctx?.rightActionsRef, ctx?.setRightReady);

  return <div ref={setRef} className={className} />;
};

export const PageHeaderActionsLeading = ({ children }) => {
  const ctx = useContext(PageHeaderContext);
  if (!ctx?.leadingReady || !ctx.leadingActionsRef.current) return null;
  return createPortal(children, ctx.leadingActionsRef.current);
};

export const PageHeaderActions = ({ children }) => {
  const ctx = useContext(PageHeaderContext);
  if (!ctx?.ready || !ctx.actionsRef.current) return null;
  return createPortal(children, ctx.actionsRef.current);
};

export const PageHeaderActionsRight = ({ children }) => {
  const ctx = useContext(PageHeaderContext);
  if (!ctx?.rightReady || !ctx.rightActionsRef.current) return null;
  return createPortal(children, ctx.rightActionsRef.current);
};

export const PageHeaderTitleSuffix = ({ children }) => {
  const setTitleSuffix = useContext(PageHeaderContext)?.setTitleSuffix;

  useEffect(() => {
    setTitleSuffix?.(children ?? null);
    return () => setTitleSuffix?.(null);
  }, [children, setTitleSuffix]);

  return null;
};

export const PageHeaderTitleOverride = ({ title, description }) => {
  const setTitleOverride = useContext(PageHeaderContext)?.setTitleOverride;
  const setDescriptionOverride = useContext(PageHeaderContext)?.setDescriptionOverride;

  useEffect(() => {
    setTitleOverride?.(title ?? null);
    return () => setTitleOverride?.(null);
  }, [title, setTitleOverride]);

  useEffect(() => {
    setDescriptionOverride?.(description ?? null);
    return () => setDescriptionOverride?.(null);
  }, [description, setDescriptionOverride]);

  return null;
};

export const usePageHeader = () => useContext(PageHeaderContext);

export const PageHeaderTitle = ({ children, className }) => {
  const ctx = useContext(PageHeaderContext);

  return (
    <h1 className={className}>
      {children}
      {ctx?.titleSuffix != null ? (
        <span className="ml-2 font-medium text-sm sm:text-base text-[var(--xl-mut)] align-middle">
          {ctx.titleSuffix}
        </span>
      ) : null}
    </h1>
  );
};
